<template>
	<div
		class="hybridlist-item"
		:class="{ 'hybridlist-item--expanded': expanded }"
	>
		<div class="grid hybridlist-item__content">
			<div
				v-if="!isSimpleLayout"
				class="col-12_md-4_smx-5 hybridlist-item__media"
			>
				<Carousel
					v-if="images && images.length"
					class="hybridlist-item__carousel"
					data-disable-slide-tab-navigation="true"
					:images="images"
					:arialabelcarouselshowlargeimages="
						arialabelcarouselshowlargeimages
					"
					:arialabelcarouselclosebutton="arialabelcarouselclosebutton"
					:arialabelcarouseldotbutton="arialabelcarouseldotbutton"
					:arialabelnext="arialabelnext"
					:arialabelprevious="arialabelprevious"
					:title="title"
				></Carousel>
				<div
					v-else
					class="hybridlist-item__pattern"
					v-html="Pattern"
				></div>
			</div>
			<div :class="isSimpleLayout ? 'col-12' : 'col-12_md-8_smx-7'">
				<div
					:class="
						isSimpleLayout
							? 'hybridlist-item__right grid simple-list'
							: 'hybridlist-item__right grid'
					"
				>
					<div class="col-12_md-4 hybridlist-item__default-group">
						<h3
							class="header-extra-small hybridlist-item__default-group__headline"
						>
							{{ title }}
						</h3>
						<div v-html="address"></div>
					</div>
					<div class="col-12_md-8 hybridlist-item__groups">
						<ul
							class="grid ul--nostyling"
							v-if="listHasEntries(data.collapsedProperties)"
						>
							<HybridlistProperty
								v-for="(
									property, index
								) in filteredCollapsedProperties"
								:key="index + '-' + property.value"
								class="hybridlist-item__group col"
								:class="sizes.collapsed[property.size]"
								:property="property"
								:propertyTypes="propertyTypes"
								:translations="translations"
							>
							</HybridlistProperty>
						</ul>
					</div>
				</div>
			</div>
			<button
				class="hybridlist-item__trigger"
				@click="toggleVisibility"
				v-if="data.expandedProperties.length"
				:aria-expanded="expanded"
				:aria-controls="title"
				:aria-label="expanded ? arialabelshowless : arialabelshowmore"
			>
				<div class="hybridlist-item__trigger-expand">
					<span class="hybridlist-item__trigger-text">
						{{ arialabelshowmore }}
					</span>
					<span class="hybridlist-item__trigger-icon">+</span>
				</div>
				<div class="hybridlist-item__trigger-collapse">
					<span class="hybridlist-item__trigger-text">
						{{ arialabelshowless }}
					</span>
					<span class="hybridlist-item__trigger-icon">-</span>
				</div>
			</button>
			<div
				class="col-12 hybridlist-item__expanded-properties"
				role="region"
				tabindex="-1"
				:id="title"
				v-if="data.expandedProperties.length"
			>
				<ul
					class="grid ul--nostyling"
					v-if="listHasEntries(data.expandedProperties)"
				>
					<HybridlistProperty
						v-for="(property, index) in filteredExpandedProperties"
						:key="index + '-' + property.value"
						class="hybridlist-item__group col"
						:class="sizes.expanded[property.size]"
						:property="property"
						:propertyTypes="propertyTypes"
						:translations="translations"
					></HybridlistProperty>
				</ul>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import Pattern from "../../../../views/visuals/leafPatternLarge.cshtml";
import Carousel from "../../carousel/carousel.vue";
import HybridlistProperty from "./hybridlistProperty.vue";

const props = defineProps([
	"data",
	"simplelist",
	"translations",
	"arialabelhidecontent",
	"arialabelshowcontent",
	"arialabelshowmore",
	"arialabelshowless",
	"arialabelcarouselshowlargeimages",
	"arialabelcarouselclosebutton",
	"arialabelcarouseldotbutton",
	"arialabelnext",
	"arialabelprevious",
]);

const propertyTypes = {
	String: 0,
	Number: 1,
	Bool: 2,
	Image: 3,
	List: 4,
};

const sizes = {
	collapsed: {
		0: "col-12_sm-6",
		1: "col-12_sm-12",
	},
	expanded: {
		0: "col-12_sm-3",
		1: "col-12_sm-10",
	},
};

const expanded = ref(false);

onMounted(() => {
	checkHash();
});

const checkHash = () => {
	const urlParams = new URLSearchParams(window.location.search);
	const listItemId = urlParams.get("listItemId");
	if (listItemId && listItemId === title.value) {
		show();
		document.querySelector(".hybridlist-item").scrollIntoView();
	}
};

const toggleVisibility = () => {
	if (expanded.value) {
		hide();
	} else {
		show();
	}
};

const show = () => {
	expanded.value = true;
	utilities.setQueryStringParameter("listItemId", title.value);
};

const hide = () => {
	expanded.value = false;
	utilities.deleteQueryStringParameter("listItemId");
};

const listHasEntries = (list) => {
	return list?.some((property) => {
		return isValidPropery(property);
	});
};

const isValidPropery = (property) => {
	return (
		property.type !== propertyTypes.Image &&
		property.name !== "title" &&
		property.name !== "Name" &&
		property.name !== "address" &&
		property.name !== "Address"
	);
};

const filteredCollapsedProperties = computed(() => {
	return props.data.collapsedProperties.filter((property) => {
		if (isValidPropery(property)) {
			return property;
		}
	});
});

const filteredExpandedProperties = computed(() => {
	return props.data.expandedProperties.filter((property) => {
		if (isValidPropery(property)) {
			return property;
		}
	});
});

const images = computed(() => {
	let images = props.data.collapsedProperties.find(
		(property) => property.type == propertyTypes.Image
	);
	if (images) {
		return images.value.map((image) => {
			let newImageSrc = image.replaceAll("width=400", "width=800");
			newImageSrc = newImageSrc.replaceAll("height=225", "height=450");
			return newImageSrc;
		});
	} else {
		return null;
	}
});

const isSimpleLayout = computed(() => {
	return props.simplelist == "True";
});

const title = computed(() => {
	let divaTitle = props.data.collapsedProperties.find(
		(property) => property.name == "Name"
	);
	let localTitle = props.data.collapsedProperties.find(
		(property) => property.name == "title" && property.value.length
	);
	if (localTitle) {
		return localTitle.value;
	} else if (divaTitle) {
		return divaTitle.value;
	} else {
		return null;
	}
});

const address = computed(() => {
	let divaAddress = props.data.collapsedProperties.find(
		(property) => property.name == "Address"
	);
	let localAddress = props.data.collapsedProperties.find(
		(property) => property.name == "address" && property.value.length
	);
	if (localAddress) {
		return localAddress.value;
	} else if (divaAddress) {
		return divaAddress.value;
	} else {
		return null;
	}
});

</script>
